import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import {
  HiOutlineBriefcase,
  HiOutlineBuildingOffice2,
  HiOutlineCheckCircle,
  HiOutlineTrash,
  HiOutlineUserGroup,
  HiOutlineVideoCamera,
} from "react-icons/hi2";

import { IconButton } from "../../../../components";
import { CATEGORY_ICONS, DEFAULT_CATEGORY_ICON } from "./categoryIcons";
import { InterviewQuestionsIcon } from "./icons";
import type { DocumentState } from "./types";

const useAnimatedValue = (target: number, durationMs = 300): number => {
  const [current, setCurrent] = useState(target);
  const prevRef = useRef(target);

  useEffect(() => {
    const from = prevRef.current;
    if (from === target) return;
    prevRef.current = target;

    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const t = Math.min((now - start) / durationMs, 1);
      const eased = 1 - (1 - t) * (1 - t);
      setCurrent(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return current;
};

const MATERIAL_ROW_KEYFRAMES = {
  "@keyframes materialSlideIn": {
    "0%": { opacity: 0, transform: "translateY(-8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
};

type ContextAreaItem = {
  key: string;
  icon: React.ElementType;
  title: string;
  description: string;
  isRequired: boolean;
};

const CONTEXT_AREAS: ContextAreaItem[] = [
  {
    key: "jobDescription",
    icon: HiOutlineBriefcase,
    title: "Job description",
    description: "Adding a job description is required.",
    isRequired: true,
  },
  {
    key: "currentInterviewProcess",
    icon: InterviewQuestionsIcon,
    title: "Current interview process",
    description: "Guides, scorecards, evaluation rubrics...",
    isRequired: false,
  },
  {
    key: "idealCandidateProfile",
    icon: HiOutlineUserGroup,
    title: "Ideal candidate profile",
    description: "Ideal candidate docs, intake notes, leveling expectations...",
    isRequired: false,
  },
  {
    key: "cultureTeamContext",
    icon: HiOutlineBuildingOffice2,
    title: "Culture & team context",
    description: "Company values, culture docs...",
    isRequired: false,
  },
  {
    key: "pastInterviewTranscripts",
    icon: HiOutlineVideoCamera,
    title: "Recording transcripts",
    description:
      "Past interviews, intake meetings, debriefs, calibration sessions...",
    isRequired: false,
  },
];

const TOTAL_CONTEXT_AREAS = CONTEXT_AREAS.length;

export type MaterialItem = {
  id: string;
  fileName: string;
  category: string;
};

type MaterialTrackerProps = {
  contextAreasProvided: number;
  jobDescriptionProvided: boolean;
  documentState: DocumentState;
  materials: MaterialItem[];
  onRemoveMaterial: (materialId: string) => void;
};

const AreaCheckIcon: React.FC<{ checked: boolean }> = ({ checked }) => (
  <Icon
    as={HiOutlineCheckCircle}
    boxSize="28px"
    color={checked ? "#60CCA5" : "#D3D8DE"}
    flexShrink={0}
  />
);

export const MaterialTracker: React.FC<MaterialTrackerProps> = ({
  contextAreasProvided,
  jobDescriptionProvided,
  documentState,
  materials,
  onRemoveMaterial,
}) => {
  const clampedAreas = Math.min(
    Math.max(contextAreasProvided, 0),
    TOTAL_CONTEXT_AREAS
  );
  const animatedPercentage = useAnimatedValue(
    (clampedAreas / TOTAL_CONTEXT_AREAS) * 100
  );

  const isAreaChecked = (key: string): boolean => {
    if (key === "jobDescription") return jobDescriptionProvided;
    return documentState[key as keyof DocumentState] ?? false;
  };

  return (
    <VStack
      align="stretch"
      spacing="0"
      h="100%"
      py="6"
      px="5"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      overflowY="auto"
    >
      {/* Quality radial + text */}
      <Flex align="center" gap="3" pb="5">
        <Box flexShrink={0}>
          <CircularProgress
            value={animatedPercentage}
            size="56px"
            thickness="6px"
            color="green.400"
            trackColor="gray.200"
          >
            <CircularProgressLabel
              fontSize="lg"
              fontWeight="semibold"
              color="gray.700"
            >
              {clampedAreas}
            </CircularProgressLabel>
          </CircularProgress>
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.800">
            {clampedAreas} of {TOTAL_CONTEXT_AREAS} context areas added
          </Text>
          <Text fontSize="xs" color="gray.500">
            Add materials to make it smarter.
          </Text>
        </Box>
      </Flex>

      {/* Added materials section */}
      <Divider borderColor="gray.200" />
      <Box pt="4" pb="4">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="#808B97"
          textTransform="uppercase"
          letterSpacing="normal"
          mb="2"
        >
          Added materials
        </Text>
        {materials.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            py="4"
            bg="#F5F6F7"
            borderRadius="6px"
          >
            <Text fontSize="sm" color="#B8BEC5" lineHeight="20px">
              Nothing added yet
            </Text>
          </Flex>
        ) : (
          <VStack align="stretch" spacing="1.5">
            {materials.map((material) => {
              const CategoryIcon =
                CATEGORY_ICONS[material.category] || DEFAULT_CATEGORY_ICON;
              return (
                <Flex
                  key={material.id}
                  align="center"
                  bg="white"
                  border="1px solid"
                  borderColor="#D3D8DE"
                  borderRadius="6px"
                  overflow="hidden"
                  h="32px"
                  sx={{
                    ...MATERIAL_ROW_KEYFRAMES,
                    animation: "materialSlideIn 250ms ease-out both",
                  }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w="32px"
                    h="32px"
                    bg="#808B97"
                    flexShrink={0}
                  >
                    <Icon as={CategoryIcon} boxSize="4" color="white" />
                  </Flex>
                  <Text
                    fontSize="sm"
                    color="#060E14"
                    lineHeight="20px"
                    flex="1"
                    noOfLines={1}
                    px="2"
                  >
                    {material.fileName}
                  </Text>
                  <IconButton
                    aria-label={`Remove ${material.fileName}`}
                    icon={<Icon as={HiOutlineTrash} boxSize="4" />}
                    size="sm"
                    variant="ghost"
                    color="#808B97"
                    _hover={{ color: "red.500", bg: "red.50" }}
                    onClick={() => onRemoveMaterial(material.id)}
                    flexShrink={0}
                    minW="24px"
                    h="24px"
                    mr="1"
                  />
                </Flex>
              );
            })}
          </VStack>
        )}
      </Box>

      {/* What's needed and what's helpful */}
      <Divider borderColor="gray.200" />
      <Box pt="4">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="#808B97"
          textTransform="uppercase"
          letterSpacing="normal"
          mb="2"
        >
          {"WHAT'S NEEDED AND WHAT'S HELPFUL"}
        </Text>
        <VStack align="stretch" spacing="2">
          {CONTEXT_AREAS.map((area) => {
            const checked = isAreaChecked(area.key);
            const descriptionColor =
              area.isRequired && !checked ? "#CA1E1B" : "#67717E";
            return (
              <Flex key={area.key} align="center" gap="3">
                <AreaCheckIcon checked={checked} />
                <Box flex="1" minW="0">
                  <Flex align="center" gap="1.5">
                    <Icon
                      as={area.icon}
                      boxSize="4"
                      color="#393F47"
                      flexShrink={0}
                    />
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="#393F47"
                      lineHeight="20px"
                    >
                      {area.title}
                      {area.isRequired && (
                        <Text
                          as="span"
                          color={checked ? "#67717E" : "#CA1E1B"}
                          fontWeight="normal"
                          ml="1"
                        >
                          (required)
                        </Text>
                      )}
                    </Text>
                  </Flex>
                  {!area.isRequired && (
                    <Text
                      fontSize="sm"
                      color={descriptionColor}
                      lineHeight="20px"
                    >
                      {area.description}
                    </Text>
                  )}
                </Box>
              </Flex>
            );
          })}
        </VStack>
      </Box>
    </VStack>
  );
};
